import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { BMICalculator } from '@/components/calculator/BMICalculator';
import { BMIResults } from '@/components/calculator/BMIResults';
import { DashboardHistory } from '@/components/dashboard/DashboardHistory';
import { Toaster } from '@/components/ui/sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calculator, LogOut, User, Sun, Moon, History, TrendingUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading: authLoading, isAuthenticated, checkAuth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [results, setResults] = useState(null);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [savingToCloud, setSavingToCloud] = useState(false);

  // Check auth on mount (if not already authenticated from callback)
  useEffect(() => {
    if (!location.state?.user && !isAuthenticated && !authLoading) {
      checkAuth();
    }
  }, [location.state, isAuthenticated, authLoading, checkAuth]);

  // Redirect if not authenticated after loading
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !location.state?.user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, location.state]);

  const handleCalculate = useCallback((data) => {
    setResults(data);
    setTimeout(() => {
      const resultsEl = document.getElementById('results');
      if (resultsEl) {
        resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const handleSaveToCloud = async () => {
    if (!results) return;

    setSavingToCloud(true);
    try {
      const response = await fetch(`${API_URL}/api/bmi/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          bmi: parseFloat(results.bmi.toFixed(1)),
          weight: results.weight,
          height: results.height,
          age: results.age,
          gender: results.gender,
          waist: results.waist,
          waist_to_height_ratio: results.waistToHeightRatio,
          who_category: results.whoCategory || 'Normal',
          athlete_mode: results.athleteMode,
          athlete_band: results.athleteBand
        })
      });

      if (!response.ok) throw new Error('Failed to save');
      
      toast.success('Saved to your account!');
      setHistoryRefresh(prev => prev + 1);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setSavingToCloud(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentUser = location.state?.user || user;

  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Dashboard"
        description="Your personal BMI tracking dashboard. View your history, analyze trends, and track your fitness progress."
        canonical="/dashboard"
        noindex={true}
      />
      {/* Dashboard Header */}
      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-primary" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg text-foreground leading-tight">BMI Calc Hub</span>
                <span className="text-xs text-muted-foreground leading-tight hidden sm:block">Dashboard</span>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <Sun className="w-5 h-5 text-muted-foreground" />
                )}
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={currentUser.picture} alt={currentUser.name} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {currentUser.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <div className="flex items-center gap-3 p-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={currentUser.picture} alt={currentUser.name} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {currentUser.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium">{currentUser.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{currentUser.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/')}>
                    <Calculator className="mr-2 h-4 w-4" />
                    Public Calculator
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Card */}
        <Card className="card-elevated mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-1">
                  Welcome back, {currentUser.name?.split(' ')[0]}!
                </h1>
                <p className="text-muted-foreground">
                  Track your BMI progress and view your history
                </p>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <span>Cloud Sync Enabled</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calculator Section */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            Calculate BMI
          </h2>
          <BMICalculator onCalculate={handleCalculate} />
        </section>

        {/* Results */}
        {results && (
          <section id="results" className="mb-8">
            <BMIResults 
              results={results} 
              onSave={handleSaveToCloud}
              saveLabel={savingToCloud ? "Saving..." : "Save to Account"}
              saveDisabled={savingToCloud}
              isAuthenticated={true}
              showSignupPrompt={false}
            />
          </section>
        )}

        {/* History Section */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Your BMI History
          </h2>
          <DashboardHistory refreshTrigger={historyRefresh} />
        </section>
      </main>

      <Footer />
      <Toaster position="top-right" />
    </div>
  );
};

export default DashboardPage;
